import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface FormState {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export const ContactPage: React.FC = () => {
  const [form, setForm] = useState<FormState>({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    // TODO: wire to POST /api/v1/contact when backend endpoint is added
    await new Promise(r => setTimeout(r, 600));
    setSubmitted(true);
    setSubmitting(false);
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <div>
      {/* Page header */}
      <section
        className="text-white text-center py-16 px-4"
        style={{ background: 'linear-gradient(135deg, #2c5530 0%, #4a7c59 100%)' }}
      >
        <h1 className="text-4xl font-semibold mb-3">Get In Touch</h1>
        <p className="text-lg opacity-90">Ready to join us? Have questions? We'd love to hear from you!</p>
      </section>

      <section className="bg-white py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">

            {/* Contact info */}
            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900">Contact Information</h2>
              {[
                { icon: '📧', label: 'Email', value: 'hello@popupyoga.org' },
                { icon: '📍', label: 'Location', value: 'We come to you! Richmond, VA and Outer Banks, NC' },
                { icon: '🕐', label: 'Response Time', value: 'We typically respond within 24 hours' },
              ].map(item => (
                <div key={item.label}>
                  <h3 className="text-base font-semibold text-green-800 mb-1">{item.icon} {item.label}</h3>
                  <p className="text-gray-600">{item.value}</p>
                </div>
              ))}
            </div>

            {/* Contact form */}
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">Send us a Message</h2>

              {submitted ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
                  <div className="text-4xl mb-3">✅</div>
                  <h3 className="text-lg font-semibold text-green-800 mb-2">Message Sent!</h3>
                  <p className="text-gray-600">Thank you for reaching out. We'll get back to you within 24 hours.</p>
                  <button
                    onClick={() => { setSubmitted(false); setForm({ name: '', email: '', subject: '', message: '' }); }}
                    className="mt-5 text-sm text-green-700 hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className={labelClass}>Name *</label>
                    <input name="name" type="text" required value={form.name} onChange={handleChange} className={inputClass} placeholder="Your full name" />
                  </div>
                  <div>
                    <label className={labelClass}>Email *</label>
                    <input name="email" type="email" required value={form.email} onChange={handleChange} className={inputClass} placeholder="your@email.com" />
                  </div>
                  <div>
                    <label className={labelClass}>Subject</label>
                    <select name="subject" value={form.subject} onChange={handleChange} className={inputClass}>
                      <option value="">Select a topic</option>
                      <option value="class-info">Class Information</option>
                      <option value="private-session">Private Session</option>
                      <option value="corporate">Corporate Events</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Message *</label>
                    <textarea name="message" required rows={5} value={form.message} onChange={handleChange} className={inputClass} placeholder="How can we help?" />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-green-800 hover:bg-green-900 text-white font-medium py-3 rounded-md transition-colors disabled:opacity-50"
                  >
                    {submitting ? 'Sending…' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-gray-800 text-white text-center py-8">
        <p className="text-gray-400 text-sm">&copy; {new Date().getFullYear()} Pop-Up Yoga. All rights reserved.</p>
        <p className="text-gray-500 text-xs mt-2">
          <Link to="/privacy" className="hover:text-gray-300 transition-colors">Privacy Policy</Link>
          {' · '}
          <Link to="/terms" className="hover:text-gray-300 transition-colors">Terms of Service</Link>
        </p>
      </footer>
    </div>
  );
};
